import { FC, useMemo, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Box, Button, Typography, IconButton, Grid, TextField, useMediaQuery } from '@mui/material';
import { Download, Cancel, Upload } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useDropzone } from 'react-dropzone';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Table, MobileTable, Loader, FileDrop, PageTitleWithBack, Page } from '../../../components';
import {
  getCompactionTestJobs,
  uploadCompactionTestFile,
  downloadCompactionTestFile
} from '../../../fetch/compaction-tests';
import { ICompactionTestJob } from '../../../models/compaction-tests';
import { formatDate, downloadFile, getDownloadFileName } from '../../../helpers';
import { CompactionTestHistoryModal } from './compaction-test-history-modal';

interface ICompactionTestJobs {}

export const CompactionTestJobs: FC<ICompactionTestJobs> = () => {
  const { neighborhoodId } = useParams<{ neighborhoodId: string }>();
  const location = useLocation<{ neighborhoodName?: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery('(max-width: 960px)');
  const neighborhoodName = location.state?.neighborhoodName || '';
  const [downloading, setDownloading] = useState<Record<number, boolean>>({});
  const [isUploading, setIsUploading] = useState<Record<number, boolean>>({});
  const [isShowingDropzone, setIsShowingDropzone] = useState<Record<number, boolean>>({});
  const [selectedFiles, setSelectedFiles] = useState<Record<number, File | null>>({});
  const [validationDates, setValidationDates] = useState<Record<number, Date | null>>({});
  const [selectedJobForHistory, setSelectedJobForHistory] = useState<ICompactionTestJob | null>(
    null
  );

  const {
    isLoading,
    data: jobs = [],
    refetch
  } = useQuery<ICompactionTestJob[], Error>(
    ['getCompactionTestJobs', neighborhoodId],
    () => getCompactionTestJobs(neighborhoodId),
    {
      enabled: !!neighborhoodId
    }
  );

  const handleDownloadFile = async (jobId: number) => {
    setDownloading(prev => ({ ...prev, [jobId]: true }));
    try {
      const response = await downloadCompactionTestFile(jobId);
      downloadFile(
        response?.data,
        getDownloadFileName(response) || `Job_${jobId}_CompactionTest.pdf`
      );

      enqueueSnackbar('File downloaded successfully!', {
        variant: 'success'
      });
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? 'Error downloading file, please try again.', {
        variant: 'error'
      });
    } finally {
      setDownloading(prev => ({ ...prev, [jobId]: false }));
    }
  };

  const handleUploadFile = async (jobId: number) => {
    const file = selectedFiles[jobId];
    const validationDate = validationDates[jobId];

    if (!file || !validationDate) {
      enqueueSnackbar('Please select both a file and a report date.', {
        variant: 'error'
      });
      return;
    }

    try {
      setIsUploading(prev => ({ ...prev, [jobId]: true }));

      const formData = new FormData();
      formData.append('compactionLetterFile', file);
      formData.append('validationDate', validationDate.toISOString());

      await uploadCompactionTestFile(jobId, formData);
      await refetch();

      enqueueSnackbar('File uploaded successfully!', {
        variant: 'success'
      });

      // Reset state
      setIsShowingDropzone(prev => ({ ...prev, [jobId]: false }));
      setSelectedFiles(prev => ({ ...prev, [jobId]: null }));
      setValidationDates(prev => ({ ...prev, [jobId]: null }));
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? 'Error uploading file, please try again.', {
        variant: 'error'
      });
    } finally {
      setIsUploading(prev => ({ ...prev, [jobId]: false }));
    }
  };

  const handleCloseHistoryModal = () => {
    setSelectedJobForHistory(null);
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Lot Number',
        accessor: 'lotNumber',
        sort: true
      },
      {
        Header: 'Date of Report',
        accessor: 'currentValidationDate',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ICompactionTestJob } };
        }) => {
          return original.currentValidationDate ? (
            <Typography>{formatDate(original.currentValidationDate)}</Typography>
          ) : (
            <Typography>No validation date</Typography>
          );
        }
      },
      {
        Header: 'Current File',
        accessor: 'hasCurrentFile',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ICompactionTestJob } };
        }) => {
          const isDownloadingForJob = downloading[original.jobId];

          if (!original.hasCurrentFile) {
            return <Typography>No file available</Typography>;
          }

          return (
            <Button
              disabled={isDownloadingForJob}
              onClick={() => handleDownloadFile(original.jobId)}
              startIcon={<Download />}
            >
              Download
            </Button>
          );
        }
      },
      {
        id: 'actions',
        Header: '',
        accessor: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ICompactionTestJob } };
        }) => {
          const isUploadingForJob = isUploading[original.jobId];
          const isDropzoneVisible = isShowingDropzone[original.jobId];

          const {
            getRootProps,
            getInputProps,
            open,
            isFocused,
            isDragActive,
            isDragAccept,
            isDragReject,
            isFileDialogActive,
            fileRejections,
            rootRef,
            inputRef,
            acceptedFiles
          } = useDropzone({
            accept: {
              'application/pdf': ['.pdf']
            },
            useFsAccessApi: false,
            onDrop: acceptedFiles => {
              if (acceptedFiles.length > 0) {
                setSelectedFiles(prev => ({ ...prev, [original.jobId]: acceptedFiles[0] }));
              } else {
                enqueueSnackbar('Invalid file type. Please upload a PDF file.', {
                  variant: 'error'
                });
              }
            }
          });

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isDropzoneVisible ? (
                <Grid container spacing={1} alignItems="center">
                  <Grid item>
                    <IconButton
                      onClick={() => {
                        setIsShowingDropzone(prev => ({
                          ...prev,
                          [original.jobId]: false
                        }));
                        setSelectedFiles(prev => ({ ...prev, [original.jobId]: null }));
                        setValidationDates(prev => ({ ...prev, [original.jobId]: null }));
                      }}
                    >
                      <Cancel />
                    </IconButton>
                  </Grid>
                  <Grid item xs>
                    {isUploadingForJob ? (
                      <Loader type="inline" position="left" />
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <FileDrop
                          id={`file-drop-${original.jobId}`}
                          getRootProps={getRootProps}
                          getInputProps={getInputProps}
                          open={open}
                          isFocused={isFocused}
                          isDragActive={isDragActive}
                          isDragAccept={isDragAccept}
                          isDragReject={isDragReject}
                          isFileDialogActive={isFileDialogActive}
                          fileRejections={fileRejections}
                          rootRef={rootRef}
                          inputRef={inputRef}
                          acceptedFiles={acceptedFiles}
                          title=""
                        >
                          <Typography variant="body2">
                            {selectedFiles[original.jobId] ? (
                              `Selected: ${selectedFiles[original.jobId]?.name}`
                            ) : (
                              <>
                                Drag and drop a PDF file or{' '}
                                <Box
                                  component="a"
                                  href="/#"
                                  sx={{
                                    color: theme => theme.palette.primary.main,
                                    textDecoration: 'none'
                                  }}
                                  onClick={e => e.preventDefault()}
                                >
                                  browse and pick
                                </Box>
                              </>
                            )}
                          </Typography>
                        </FileDrop>

                        <DatePicker
                          label="Date of Report"
                          value={validationDates[original.jobId]}
                          onChange={newValue => {
                            setValidationDates(prev => ({ ...prev, [original.jobId]: newValue }));
                          }}
                          slots={{
                            textField: TextField
                          }}
                          slotProps={{
                            textField: { size: 'small' }
                          }}
                        />

                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleUploadFile(original.jobId)}
                          disabled={
                            !selectedFiles[original.jobId] || !validationDates[original.jobId]
                          }
                          startIcon={<Upload />}
                        >
                          Upload File
                        </Button>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Upload />}
                    onClick={() =>
                      setIsShowingDropzone(prev => ({
                        ...prev,
                        [original.jobId]: true
                      }))
                    }
                  >
                    {original.hasCurrentFile ? 'Replace File' : 'Upload File'}
                  </Button>
                  <Button onClick={() => setSelectedJobForHistory(original)}>History</Button>
                </Box>
              )}
            </Box>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    downloading,
    isUploading,
    isShowingDropzone,
    selectedFiles,
    validationDates,
    enqueueSnackbar
  ]);

  const pageTitle = neighborhoodName
    ? `Compaction Test By Jobs - ${neighborhoodName}`
    : 'Compaction Test By Jobs';

  if (!neighborhoodId) {
    return (
      <Page title={pageTitle}>
        <Typography>No neighborhood selected</Typography>
      </Page>
    );
  }

  return (
    <Page title={pageTitle}>
      <PageTitleWithBack
        title={pageTitle}
        backText="Back to Neighborhoods"
        backUrl={'/vendors/compaction-tests'}
      />
      <Table
        columns={columns}
        data={jobs}
        isLoading={isLoading}
        hidePagination
        mobileProps={{
          fields: columns
            .filter(c => c.Header || c.Cell)
            .map(c => ({
              name: (c.Header as string) || 'Actions',
              accessor: c.accessor as string,
              Cell: c?.Cell
            }))
        }}
        ResponsiveComponent={isMobile ? MobileTable : undefined}
      />

      <CompactionTestHistoryModal
        jobId={selectedJobForHistory?.jobId || null}
        lotNumber={selectedJobForHistory?.lotNumber}
        onClose={handleCloseHistoryModal}
      />
    </Page>
  );
};
